import React, { useEffect, useState } from 'react';
import { timeAgo, fmtDate, fmtDateTime, parseUTC } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { logApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { Btn, Alert, Spinner, EmptyState } from '../components/ui';

function ReverseModal({ action, onClose, onDone }) {
  const [note,   setNote]   = useState('');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const submit = async () => {
    setSaving(true); setError('');
    try {
      await logApi.reverseAction(action.id, note);
      onDone();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to reverse action');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '16px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '17px', fontWeight: 600, color: 'var(--color-primary)' }}>Reverse Deadline Change</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: 'var(--color-text-muted)' }}>×</button>
        </div>
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '4px' }}>
          Admin: <strong>{action.admin?.name}</strong>
        </p>
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '12px' }}>
          Changed deadline from <strong>{action.oldValue}</strong> to <strong>{action.newValue}</strong>
        </p>
        <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '8px' }}>
          This will restore the deadline to <strong>{action.oldValue}</strong> and notify the Admin.
        </p>
        {error && <Alert type="error" message={error} />}
        <div style={{ marginTop: '12px', marginBottom: '16px' }}>
          <label style={{ fontSize: '13px', fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: '6px' }}>
            Reason for reversal (optional)
          </label>
          <textarea value={note} onChange={e => setNote(e.target.value)}
            placeholder="Explain why you are reversing this change..."
            rows={2} style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px', resize: 'vertical' }} />
        </div>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn variant="danger" onClick={submit} loading={saving}>Reverse This Change</Btn>
        </div>
      </div>
    </div>
  );
}

export default function AdminActionsLog() {
  const { user }             = useAuth();
  const navigate             = useNavigate();
  const [logs,    setLogs]   = useState([]);
  const [loading, setLoading]= useState(true);
  const [error,   setError]  = useState('');
  const [search,  setSearch] = useState('');
  const [reverseTarget, setReverseTarget] = useState(null);

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const load = () => {
    logApi.getAdminActions()
      .then(r => setLogs(r.data.data || []))
      .catch(() => setError('Failed to load admin actions'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = logs.filter(l =>
    !search ||
    l.admin?.name?.toLowerCase().includes(search.toLowerCase()) ||
    l.taskTitle?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ padding: '24px', maxWidth: '800px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={S.title}>Admin Actions Log</h1>
        <p style={S.sub}>
          {isSuperAdmin
            ? 'All Admin deadline changes and task deletions across the company'
            : 'Your own deadline changes and task deletions'}
        </p>
      </div>

      {error && <Alert type="error" message={error} />}

      <input
        placeholder="Search by Admin name or task..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        style={{ ...S.input, marginBottom: '16px' }}
      />

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}><Spinner /></div>
      ) : filtered.length === 0 ? (
        <EmptyState icon="🔍" title="No admin actions yet" subtitle="Deadline changes and task deletions by Admins will appear here." />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filtered.map(log => (
            <div key={log.id} style={{
              background: log.reversed ? '#F0FFF4' : 'var(--color-surface)',
              borderRadius: 'var(--radius-md)',
              border: `1px solid ${log.reversed ? '#9AE6B4' : log.actionType === 'TASK_DELETED' ? '#FEB2B2' : 'var(--color-border)'}`,
              padding: '14px 16px',
              borderLeft: `4px solid ${log.reversed ? 'var(--color-success)' : log.actionType === 'TASK_DELETED' ? 'var(--color-danger)' : 'var(--color-warning)'}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '4px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 600 }}>{log.admin?.name}</span>
                    <span style={{
                      fontSize: '11px', padding: '2px 8px', borderRadius: '999px', fontWeight: 600,
                      background: log.actionType === 'TASK_DELETED' ? '#FFF5F5' : '#FFFBEB',
                      color:      log.actionType === 'TASK_DELETED' ? 'var(--color-danger)' : '#744210',
                    }}>
                      {log.actionType === 'TASK_DELETED' ? 'DELETED TASK' : 'CHANGED DEADLINE'}
                    </span>
                    {log.reversed && (
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', background: '#F0FFF4', color: 'var(--color-success)', fontWeight: 600 }}>
                        REVERSED
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginBottom: '2px' }}>
                    Task: <strong>{log.taskTitle || log.taskId?.toString().slice(0, 8) + '...'}</strong>
                  </p>
                  {log.actionType === 'DEADLINE_CHANGED' && (
                    <p style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                      {log.oldValue} → <strong>{log.newValue}</strong>
                    </p>
                  )}
                  {log.reversed && log.reversalNote && (
                    <p style={{ fontSize: '12px', color: 'var(--color-success)', marginTop: '4px', fontStyle: 'italic' }}>
                      Reversed by {log.reversedBy?.name}: "{log.reversalNote}"
                    </p>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 }}>
                  <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>{timeAgo(log.timestamp)}</span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    {log.taskId && (
                      <button onClick={() => navigate(`/tasks/${log.taskId}`)} style={{
                        background: 'none', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                        padding: '4px 10px', fontSize: '12px', cursor: 'pointer', color: 'var(--color-accent)',
                      }}>View task</button>
                    )}
                    {isSuperAdmin && !log.reversed && log.actionType === 'DEADLINE_CHANGED' && (
                      <button onClick={() => setReverseTarget(log)} style={{
                        background: 'none', border: '1px solid #FEB2B2', borderRadius: 'var(--radius-sm)',
                        padding: '4px 10px', fontSize: '12px', cursor: 'pointer', color: 'var(--color-danger)',
                      }}>Reverse</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {reverseTarget && (
        <ReverseModal
          action={reverseTarget}
          onClose={() => setReverseTarget(null)}
          onDone={() => { setReverseTarget(null); load(); }}
        />
      )}
    </div>
  );
}

const S = {
  title: { fontSize: '22px', fontWeight: 600, color: 'var(--color-primary)', marginBottom: '2px' },
  sub:   { fontSize: '13px', color: 'var(--color-text-muted)' },
  input: { padding: '10px 14px', borderRadius: 'var(--radius-md)', border: '1.5px solid var(--color-border)', fontSize: '14px', width: '100%' },
};
